import { Backpack, Footprints, Shirt } from "lucide-react";

export const STORAGE_KEY = "parakh-return-items-v1";

export const REQUIRED_COLUMNS = [
  "order_id",
  "barcode",
  "product_name",
  "category",
  "variant",
  "mrp",
  "return_reason",
  "image_url",
];

export const QUESTIONS = [
  {
    id: "identity",
    hindi: "Product sahi item aur variant hai?",
    english: "Is it the correct product and variant?",
  },
  {
    id: "cleanliness",
    hindi: "Product par daag ya gandagi hai?",
    english: "Are there stains or dirt?",
  },
  {
    id: "damage",
    hindi: "Product phata, toota, ya damaged hai?",
    english: "Is the product torn, broken, or damaged?",
  },
  {
    id: "tags",
    hindi: "Tags aur zaroori parts complete hain?",
    english: "Are the tags and required components complete?",
  },
  {
    id: "altered",
    hindi: "Product used ya altered lag raha hai?",
    english: "Does the product appear used or altered?",
  },
];

export const ANSWERS = [
  { value: "good", hindi: "Theek hai", english: "Good", tone: "good" },
  { value: "minor", hindi: "Halka issue", english: "Minor issue", tone: "minor" },
  { value: "major", hindi: "Bada issue", english: "Major issue", tone: "major" },
];

const DEMO_EVIDENCE = {
  jeans: "Oil stain evidence",
  tshirt: "Seam tear evidence",
  kurta: "Missing button evidence",
  sneakers: "Sole wear evidence",
  handbag: "Detached strap evidence",
};

const answerSet = (identity, cleanliness, damage, tags, altered) => ({
  identity: { value: identity },
  cleanliness: { value: cleanliness },
  damage: { value: damage },
  tags: { value: tags },
  altered: { value: altered },
});

export const DEMO_RETURNS = [
  {
    order_id: "RET-2026-001",
    barcode: "PKH-JNS-032",
    product_name: "Straight-Fit Denim Jeans",
    category: "Apparel",
    variant: "32 / Blue",
    mrp: 2499,
    return_reason: "Size did not fit",
    image_url: "images/catalog-denim-jeans-blue.png",
    demo: true,
    inspection: null,
  },
  {
    order_id: "RET-2026-002",
    barcode: "PKH-JNS-034",
    product_name: "Straight-Fit Denim Jeans",
    category: "Apparel",
    variant: "34 / Blue",
    mrp: 2499,
    return_reason: "Customer reported a dark mark",
    image_url: "images/catalog-denim-jeans-blue.png",
    demo: true,
    inspection: {
      answers: answerSet("good", "minor", "good", "good", "good"),
      evidence: { cleanliness: { label: DEMO_EVIDENCE.jeans, dataUrl: "" } },
      grade: "GRADE_B",
      disposition: "Donate",
      reasons: ["Minor stain or dirt"],
      submitted_at: "2026-08-21T09:20:00.000Z",
      review_status: "Unreviewed",
      flag_note: "",
    },
  },
  {
    order_id: "RET-2026-003",
    barcode: "PKH-TSH-M01",
    product_name: "Cotton Crew-Neck T-Shirt",
    category: "Apparel",
    variant: "M / Rust",
    mrp: 999,
    return_reason: "Size too small",
    image_url: "images/catalog-tshirt-rust.png",
    demo: true,
    inspection: {
      answers: answerSet("good", "good", "major", "good", "good"),
      evidence: { damage: { label: DEMO_EVIDENCE.tshirt, dataUrl: "" } },
      grade: "GRADE_D",
      disposition: "Dispose",
      reasons: ["Major damage"],
      submitted_at: "2026-08-21T10:10:00.000Z",
      review_status: "Unreviewed",
      flag_note: "",
    },
  },
  {
    order_id: "RET-2026-004",
    barcode: "PKH-KRT-M01",
    product_name: "Everyday Cotton Kurta",
    category: "Apparel",
    variant: "M / Ivory",
    mrp: 1799,
    return_reason: "Button was missing",
    image_url: "images/catalog-kurta-ivory.png",
    demo: true,
    inspection: {
      answers: answerSet("good", "good", "good", "minor", "good"),
      evidence: { tags: { label: DEMO_EVIDENCE.kurta, dataUrl: "" } },
      grade: "GRADE_C",
      disposition: "Rework",
      reasons: ["Tag or required component missing"],
      submitted_at: "2026-08-20T14:15:00.000Z",
      review_status: "Flagged",
      flag_note: "Please confirm the replacement button can be matched.",
    },
  },
  {
    order_id: "RET-2026-005",
    barcode: "PKH-SNK-UK8",
    product_name: "Minimal Low-Top Sneakers",
    category: "Footwear",
    variant: "UK 8 / White",
    mrp: 3299,
    return_reason: "Fit was uncomfortable",
    image_url: "images/catalog-sneakers-white.png",
    demo: true,
    inspection: {
      answers: answerSet("good", "good", "good", "good", "minor"),
      evidence: { altered: { label: DEMO_EVIDENCE.sneakers, dataUrl: "" } },
      grade: "ALTERED",
      disposition: "Dispose",
      reasons: ["Product appears used or altered"],
      submitted_at: "2026-08-20T12:30:00.000Z",
      review_status: "Agreed",
      flag_note: "",
    },
  },
  {
    order_id: "RET-2026-006",
    barcode: "PKH-BAG-TAN",
    product_name: "Structured Everyday Handbag",
    category: "Bags",
    variant: "One Size / Tan",
    mrp: 2199,
    return_reason: "Shoulder strap detached",
    image_url: "images/catalog-handbag-tan.png",
    demo: true,
    inspection: {
      answers: answerSet("good", "good", "major", "good", "good"),
      evidence: { damage: { label: DEMO_EVIDENCE.handbag, dataUrl: "" } },
      grade: "GRADE_D",
      disposition: "Dispose",
      reasons: ["Major damage"],
      submitted_at: "2026-08-19T16:45:00.000Z",
      review_status: "Agreed",
      flag_note: "",
    },
  },
];

export function getProductIcon(category, className = "") {
  if (category === "Footwear") {
    return <Footprints className={className} aria-hidden="true" />;
  }

  if (category === "Bags") {
    return <Backpack className={className} aria-hidden="true" />;
  }

  return <Shirt className={className} aria-hidden="true" />;
}

export function getInspectionResult(answers) {
  const valueFor = (questionId) => answers[questionId]?.value;
  const hasMajor = Object.values(answers).some((answer) => answer?.value === "major");
  const hasMinor = Object.values(answers).some((answer) => answer?.value === "minor");
  const reasons = [];

  if (valueFor("altered") !== "good") {
    reasons.push("Product appears used or altered");
    return { grade: "ALTERED", disposition: "Dispose", reasons };
  }

  if (hasMajor) {
    reasons.push("Major damage or condition issue");
    return { grade: "GRADE_D", disposition: "Dispose", reasons };
  }

  if (valueFor("tags") !== "good") {
    reasons.push("Tag or required component missing");
    return { grade: "GRADE_C", disposition: "Rework", reasons };
  }

  if (hasMinor) {
    reasons.push("Minor stain, dirt, or wear");
    return { grade: "GRADE_B", disposition: "Donate", reasons };
  }

  return { grade: "GRADE_A", disposition: "Restock", reasons: ["All checks passed"] };
}

export function parseCsv(text) {
  const rows = [];
  let cell = "";
  let row = [];
  let quoted = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    const nextCharacter = text[index + 1];

    if (character === '"' && quoted && nextCharacter === '"') {
      cell += '"';
      index += 1;
    } else if (character === '"') {
      quoted = !quoted;
    } else if (character === "," && !quoted) {
      row.push(cell.trim());
      cell = "";
    } else if ((character === "\n" || character === "\r") && !quoted) {
      if (character === "\r" && nextCharacter === "\n") {
        index += 1;
      }
      row.push(cell.trim());
      if (row.some((value) => value)) {
        rows.push(row);
      }
      row = [];
      cell = "";
    } else {
      cell += character;
    }
  }

  row.push(cell.trim());
  if (row.some((value) => value)) {
    rows.push(row);
  }

  return rows;
}

export function validateAndFormatCsv(text, existingOrderIds) {
  const rows = parseCsv(text);
  const headers = rows[0]?.map((header) => header.trim().toLowerCase()) || [];
  const missing = REQUIRED_COLUMNS.filter((column) => !headers.includes(column));

  if (!rows.length) {
    return { error: "This file is empty. Choose a CSV with a header row and returns." };
  }

  if (missing.length) {
    return { error: `Missing required columns: ${missing.join(", ")}.` };
  }

  const importedIds = new Set();
  const errors = [];
  const items = rows.slice(1).map((row, rowIndex) => {
    const rawItem = Object.fromEntries(headers.map((header, index) => [header, row[index] || ""]));
    const item = {
      ...rawItem,
      mrp: Number(rawItem.mrp),
      demo: false,
      inspection: null,
    };
    const line = rowIndex + 2;

    if (REQUIRED_COLUMNS.some((column) => !String(rawItem[column]).trim())) {
      errors.push(`Row ${line}: complete every required field.`);
    }
    if (!["Apparel", "Footwear", "Bags"].includes(rawItem.category)) {
      errors.push(`Row ${line}: category must be Apparel, Footwear, or Bags.`);
    }
    if (!Number.isFinite(item.mrp) || item.mrp <= 0) {
      errors.push(`Row ${line}: MRP must be a positive number.`);
    }
    if (existingOrderIds.has(item.order_id) || importedIds.has(item.order_id)) {
      errors.push(`Row ${line}: order ID ${item.order_id} already exists.`);
    }
    importedIds.add(item.order_id);
    return item;
  });

  if (!items.length) {
    return { error: "No return rows were found below the header." };
  }

  if (errors.length) {
    return { error: errors.slice(0, 4).join(" ") };
  }

  return { items };
}

export const formatRupees = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);